'use client';

import React, { useMemo, useState } from 'react';

interface RecipesSidebarProps {
  sections: Record<string, string[]>;
  search: string;
  onSearchChange: (value: string) => void;
  selectedItemsBySection?: Record<string, string[]>;
  onSectionItemClick?: (section: string, item: string) => void;
  className?: string;
}

const RecipesSidebar: React.FC<RecipesSidebarProps> = ({
  sections,
  search,
  onSearchChange,
  selectedItemsBySection = {},
  onSectionItemClick,
  className = '',
}) => {
  const [expandedSections, setExpandedSections] = useState<string[]>([]);

  // Colores por sección para la barra vertical y caret, inspirados en ProductSidebar
  const sectionColors: Record<string, string> = useMemo(
    () => ({
      PLATOS: '#E15E30',
      DIFICULTAD: '#8D766F',
      DURACIÓN: '#BFCF7A',
      INGREDIENTES: '#CEB0FF',
    }),
    []
  );

  // Abrir todas las secciones por defecto al primer render cuando existan
  React.useEffect(() => {
    const keys = Object.keys(sections || {});
    if (keys.length > 0) {
      setExpandedSections(keys);
    }
  }, [sections]);

  const toggleSection = (name: string) => {
    setExpandedSections((prev) =>
      prev.includes(name) ? prev.filter((s) => s !== name) : [...prev, name]
    );
  };

  return (
    <aside data-component-id="src/components/sections/recipes/RecipesSidebar.tsx:50:4" data-component-path="src/components/sections/recipes/RecipesSidebar.tsx" data-component-line="50" data-component-file="RecipesSidebar.tsx" data-component-name="aside" data-component-content="%7B%22elementName%22%3A%22aside%22%7D" className={`w-full md:w-[280px] flex-shrink-0 ${className}`}>
      <div data-component-id="src/components/sections/recipes/RecipesSidebar.tsx:51:6" data-component-path="src/components/sections/recipes/RecipesSidebar.tsx" data-component-line="51" data-component-file="RecipesSidebar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-%5B%23FEF5E6%5D%20rounded-%5B20px%5D%20pr-4%20py-6%20pl-0%22%7D" className="bg-[#FEF5E6] rounded-[20px] pr-4 py-6 pl-0">
        {Object.entries(sections)
          .filter(([section]) => section.toUpperCase() !== 'INGREDIENTES')
          .map(([section, items]) => {
          const color = sectionColors[section.toUpperCase()] || '#E15E30';
          const isOpen = expandedSections.includes(section);
          return (
            <div data-component-id="src/components/sections/recipes/RecipesSidebar.tsx:58:12" data-component-path="src/components/sections/recipes/RecipesSidebar.tsx" data-component-line="58" data-component-file="RecipesSidebar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%20mb-4%22%7D" key={section} className="relative mb-4">
              {/* Barra vertical de color */}
              <div data-component-id="src/components/sections/recipes/RecipesSidebar.tsx:60:14" data-component-path="src/components/sections/recipes/RecipesSidebar.tsx" data-component-line="60" data-component-file="RecipesSidebar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
                className={`absolute left-0 top-0 w-1 transition-all duration-300 ${
                  isOpen ? 'h-full' : 'h-5'
                }`}
                style={{ backgroundColor: color }}
              />

              {/* Título y caret */}
              <div data-component-id="src/components/sections/recipes/RecipesSidebar.tsx:68:14" data-component-path="src/components/sections/recipes/RecipesSidebar.tsx" data-component-line="68" data-component-file="RecipesSidebar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-row%20items-center%20justify-between%20mb-2%20ml-4%22%7D" className="flex flex-row items-center justify-between mb-2 ml-4">
                <span data-component-id="src/components/sections/recipes/RecipesSidebar.tsx:69:16" data-component-path="src/components/sections/recipes/RecipesSidebar.tsx" data-component-line="69" data-component-file="RecipesSidebar.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22font-bold%20text-base%20leading-5%20text-global-1%20uppercase%20tracking-wide%20cursor-pointer%20hover%3Atext-global-2%20transition-colors%22%7D"
                  className="font-bold text-base leading-5 text-global-1 uppercase tracking-wide cursor-pointer hover:text-global-2 transition-colors"
                  style={{ fontFamily: "'TT_Norms_Pro-Regular', Helvetica" }}
                  onClick={() => toggleSection(section)}
                >
                  {section}
                </span>
                <svg data-component-id="src/components/sections/recipes/RecipesSidebar.tsx:76:16" data-component-path="src/components/sections/recipes/RecipesSidebar.tsx" data-component-line="76" data-component-file="RecipesSidebar.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%7D"
                  width="16"
                  height="9"
                  viewBox="0 0 16 9"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className={`cursor-pointer transition-transform ${isOpen ? '' : '-rotate-90'}`}
                  onClick={() => toggleSection(section)}
                >
                  <path data-component-id="src/components/sections/recipes/RecipesSidebar.tsx:85:18" data-component-path="src/components/sections/recipes/RecipesSidebar.tsx" data-component-line="85" data-component-file="RecipesSidebar.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D" d="M1 1L8 8L15 1" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>

              {/* Items (subcategorías) */}
              {isOpen && (
                <div data-component-id="src/components/sections/recipes/RecipesSidebar.tsx:91:16" data-component-path="src/components/sections/recipes/RecipesSidebar.tsx" data-component-line="91" data-component-file="RecipesSidebar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-0%20ml-4%20mb-3%22%7D" className="space-y-0 ml-4 mb-3">
                  {items.map((item, idx) => (
                    <div data-component-id="src/components/sections/recipes/RecipesSidebar.tsx:93:20" data-component-path="src/components/sections/recipes/RecipesSidebar.tsx" data-component-line="93" data-component-file="RecipesSidebar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22cursor-pointer%20hover%3Atext-global-2%20transition-colors%20duration-200%22%7D"
                      key={`${section}-${idx}`}
                      onClick={() => onSectionItemClick?.(section, item)}
                      className="cursor-pointer hover:text-global-2 transition-colors duration-200"
                      style={{
                        fontFamily: "'TT_Norms_Pro-Medium', Helvetica",
                        fontSize: '14px',
                        lineHeight: '22px',
                        letterSpacing: '0%',
                        color:
                          selectedItemsBySection[section]?.includes(item)
                            ? 'rgba(61, 28, 26, 1)'
                            : 'rgba(61, 28, 26, 0.6)',
                        fontWeight:
                          selectedItemsBySection[section]?.includes(item) ? 700 : 500,
                      }}
                    >
                      {item}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
          })}

        {/* Buscador */}
        <div data-component-id="src/components/sections/recipes/RecipesSidebar.tsx:120:8" data-component-path="src/components/sections/recipes/RecipesSidebar.tsx" data-component-line="120" data-component-file="RecipesSidebar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%20mt-2%22%7D" className="relative mt-2">
          <input data-component-id="src/components/sections/recipes/RecipesSidebar.tsx:121:10" data-component-path="src/components/sections/recipes/RecipesSidebar.tsx" data-component-line="121" data-component-file="RecipesSidebar.tsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22type%22%3A%22text%22%2C%22value%22%3A%22%5Bvar%3Asearch%5D%22%2C%22className%22%3A%22w-full%20pl-10%20pr-3%20py-2%20rounded-%5B10px%5D%20bg-white%20text-global-1%20font-tt-norms%20text-sm%20outline-none%20border%20border-%5B%233D1C1A%5D%2F20%20placeholder-%5B%238D766F%5D%22%7D"
            type="text"
            placeholder="Buscar receta"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full pl-10 pr-3 py-2 rounded-[10px] bg-white text-global-1 font-tt-norms text-sm outline-none border border-[#3D1C1A]/20 placeholder-[#8D766F]"
          />
          <svg data-component-id="src/components/sections/recipes/RecipesSidebar.tsx:128:10" data-component-path="src/components/sections/recipes/RecipesSidebar.tsx" data-component-line="128" data-component-file="RecipesSidebar.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%2C%22className%22%3A%22absolute%20left-3%20top-1%2F2%20-translate-y-1%2F2%20w-4%20h-4%20text-%5B%238D766F%5D%22%7D"
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8D766F]"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path data-component-id="src/components/sections/recipes/RecipesSidebar.tsx:134:12" data-component-path="src/components/sections/recipes/RecipesSidebar.tsx" data-component-line="134" data-component-file="RecipesSidebar.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D"
              d="M8.5 15a6.5 6.5 0 1 0 0-13 6.5 6.5 0 0 0 0 13Zm0 0 6 3"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </div>
    </aside>
  );
};

export default RecipesSidebar;


